import React from 'react';
import { iconsPath, imagePath } from '../../utils/imagePath';
import { useNavigate } from 'react-router-dom';
import { HelpCenterData } from '../../components/data/data/HelpCenter/HelpCenterData';
import { FAQSection } from '../../components/3D_Home/FAQSection';
import UnifiedSearchBar from '../../components/HelpCenterComponents/UnifiedSearchBar';
import { trandingTopics } from '../../components/data/data/TrendingTopics/TrendingTopicsData';

const HelpCenter = () => {
  const navigate = useNavigate();
  const [isSearching, setIsSearching] = React.useState(false);

  // ✅ Holds the submitted search results + query (set when user presses Enter)
  const [submittedSearch, setSubmittedSearch] = React.useState(null);

  const handleSearchSubmit = (payload) => {
    // payload is { query, results } on Enter, or null on clear
    setSubmittedSearch(payload);
  };

  const handleSearchChange = (query) => {
    setIsSearching(!!query);
    // If user clears the input, also clear inline results
    if (!query) setSubmittedSearch(null);
  };

  // ✅ Navigate on inline result click
  const handleInlineResultClick = (result) => {
    if (result.anchor) {
      navigate(result.path, { state: { scrollTo: result.anchor } });
    } else {
      navigate(result.path);
    }
  };

  const hasInlineResults = submittedSearch && submittedSearch.results;

  const totalInlineResults = hasInlineResults
    ? submittedSearch.results.categories.length +
      submittedSearch.results.articles.length +
      submittedSearch.results.sections.length +
      submittedSearch.results.documentation.length
    : 0;

  return (
    <div className="w-full max-w-screen-2xl mx-auto px-4 md:px-10 lg:px-16 xl:px-24 py-[14vh] md:py-[20vh] text-white">
      {/* Help Center Header */}
      <div className="flex justify-center items-center gap-6 mb-6">
        <img
          src={imagePath?.logo_small}
          alt="logo_small"
          className="w-[30px] md:w-[40px] lg:w-[50px] object-contain"
        />
        <div className="h-[30px] md:h-[40px] border-l border-[#FFFFFF]"></div>
        <div className="text-[clamp(1.25rem,2vw,2rem)] font-light font-jakarta">
          HELP CENTER
        </div>
      </div>

      {/* Search Bar */}
      <div className="text-[clamp(1.5rem,3vw,2.5rem)] text-center font-bold mb-6">
        What can I help you find?
      </div>

      <UnifiedSearchBar
        placeholder="Search the Spatial Grid Help Center"
        variant="full"
        className="mb-10"
        showResultsCount={true}
        onSearchChange={handleSearchChange}
        onSearchSubmit={handleSearchSubmit}
      />

      {/* ✅ Inline Search Results (shown after Enter) */}
      {hasInlineResults && (
        <div className="w-full max-w-4xl mx-auto mb-16">
          {totalInlineResults > 0 ? (
            <>
              <h2 className="text-lg font-semibold text-[#C1C1C1] mb-4">
                Results for "{submittedSearch.query}"
              </h2>

              {submittedSearch.results.categories.length > 0 && (
                <InlineResultSection
                  title="Categories"
                  results={submittedSearch.results.categories}
                  onResultClick={handleInlineResultClick}
                />
              )}
              {submittedSearch.results.articles.length > 0 && (
                <InlineResultSection
                  title="Articles"
                  results={submittedSearch.results.articles}
                  onResultClick={handleInlineResultClick}
                />
              )}
              {submittedSearch.results.sections.length > 0 && (
                <InlineResultSection
                  title="Sections"
                  results={submittedSearch.results.sections}
                  onResultClick={handleInlineResultClick}
                />
              )}
              {submittedSearch.results.documentation.length > 0 && (
                <InlineResultSection
                  title="Documentation"
                  results={submittedSearch.results.documentation}
                  onResultClick={handleInlineResultClick}
                />
              )}
            </>
          ) : (
            <p className="text-center text-[#C1C1C1]">
              No results found for "{submittedSearch.query}". Try different
              keywords.
            </p>
          )}
        </div>
      )}

      {/* Hide categories/trending while searching or showing inline results */}
      {!isSearching && !hasInlineResults && (
        <>
          {/* Help Center Categories */}
          <Section side="center">
            <Grid data={HelpCenterData} onClick={(path) => navigate(path)} />
          </Section>

          {/* Trending Topics */}
          <Section title="Trending Topics">
            <div className="w-full flex justify-center">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1 w-full max-w-5xl md:max-w-6xl">
                {trandingTopics.map((v, i) => (
                  <div
                    key={i}
                    onClick={() => {
                      if (v?.redirectTo) {
                        navigate(v.redirectTo, {
                          state: { scrollTo: v.redirectAnchor },
                        });
                      } else {
                        navigate(`/documentation/trending/${i}`);
                      }
                    }}
                    className="w-full px-4 py-3 md:p-6 text-white flex items-center justify-start rounded-lg cursor-pointer transition hover:bg-[#31333A]"
                  >
                    <p className="w-full text-base md:text-md flex items-center justify-between gap-3">
                      {v?.title}
                      <img src={iconsPath?.rightArrowRed} alt="Right Arrow" />
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </Section>
        </>
      )}

      {/* FAQ always visible */}
      <FAQSection
        title={
          <div className="text-[clamp(1.25rem,2.5vw,2.25rem)] text-center py-10 font-bold text-[#D8D8D8]">
            Frequently Asked Questions
          </div>
        }
        containerClass="w-full"
        gridClass="grid grid-cols-1 xl:grid-cols-2 gap-8 items-start"
        limit={8}
      />
    </div>
  );
};

/* ✅ Inline result section rendered in the page body */
const InlineResultSection = ({ title, results, onResultClick }) => (
  <div className="mb-8">
    <h3 className="text-xs font-semibold text-gray-400 uppercase mb-3 tracking-widest">
      {title}
    </h3>
    <div className="flex flex-col gap-2">
      {results.map((result, index) => (
        <div
          key={index}
          onClick={() => onResultClick(result)}
          className="px-5 py-4 bg-[#31333A] hover:bg-[#3a3c43] rounded-lg cursor-pointer transition-colors flex flex-col gap-1"
        >
          <div className="text-sm font-medium text-white">{result.title}</div>
          {result.description && (
            <div className="text-xs text-gray-400">{result.description}</div>
          )}
          {result.articleTitle && (
            <div className="text-xs text-gray-400">
              in {result.articleTitle}
            </div>
          )}
          {result.categoryName && (
            <div className="text-xs text-[#EE2B2A]">{result.categoryName}</div>
          )}
          {/* {result.type === 'documentation' && (
            <div className="text-xs text-[#519FFF]">📄 Documentation</div>
          )} */}
        </div>
      ))}
    </div>
  </div>
);

const Section = ({ title, children, side = 'left' }) => (
  <div className="w-full my-16 flex flex-col items-center justify-center">
    {title && (
      <h2
        className="text-[clamp(1.25rem,2.5vw,2rem)] font-semibold mb-4"
        style={{ textAlign: side }}
      >
        {title}
      </h2>
    )}
    {children}
  </div>
);

const Grid = ({ data, onClick }) => (
  <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 md:gap-6 lg:gap-8 w-full">
    {data.map((v, i) => (
      <div
        key={i}
        onClick={() => v?.path && onClick && onClick(v?.path)}
        className="w-full aspect-square max-w-[324px] mx-auto rounded-xl flex flex-col items-center justify-center text-center px-3 sm:px-4 cursor-pointer transition-colors"
      >
        {v?.icon && (
          <img
            src={v.icon}
            alt={v.title}
            className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 mb-3 sm:mb-4 object-contain"
          />
        )}
        <p className="text-sm sm:text-base md:text-lg font-bold leading-snug font-jakarta">
          {v?.title}
        </p>
        {v?.desc && (
          <p className="text-xs sm:text-sm md:text-base text-[#FFFFFF] font-light mt-1 sm:mt-2 leading-snug">
            {v?.desc}
          </p>
        )}
      </div>
    ))}
  </div>
);

export default HelpCenter;
